import { Global } from "@emotion/react";
import { Box, CssBaseline, Divider, MenuItem } from "@mui/material";
import {
  AccountButton,
  AuthProvider,
  Navbar,
  type OidcUser,
  useAuth,
} from "@ssf/auth-components";
import {
  AppWrapper,
  Footer,
  Page,
  queryClient,
  sisTheme,
} from "@ssf/components";
import bgImageAvif from "@ssf/components/assets/bg.avif";
import bgImageJpg from "@ssf/components/assets/bg.jpg";
import bgImageWebp from "@ssf/components/assets/bg.webp";
import { setAuthorization as setMemdbAuthorization } from "@ssf/memdb-api";
import { trpcClient, trpcContext } from "@ssf/play-components";
import { QueryClientProvider } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import {
  AutoAsk,
  Header,
  HeaderMenu,
  Router,
  TeamSearch,
  TeamSearchMenuItem,
  useTeamSearch,
} from "./components";
import { generatePath, i18next, setAuthorization } from "./lib/index.js";

const SIGNIN_REDIRECT_ARGS = { extraQueryParams: { createAccount: true } };
const onUser = (user: OidcUser | null | undefined) => {
  setMemdbAuthorization(user?.access_token);
  setAuthorization(user?.access_token);
};

export const App = () => (
  <AppWrapper env={import.meta.env} theme={sisTheme} i18n={i18next}>
    <CssBaseline />
    <QueryClientProvider client={queryClient}>
      <trpcContext.TRPCProvider
        trpcClient={trpcClient}
        queryClient={queryClient}
      >
        <AuthProvider signinRedirectArgs={SIGNIN_REDIRECT_ARGS} onUser={onUser}>
          <Global
            styles={{
              body: {
                backgroundImage: `url(${bgImageJpg})`,
                "html.webp &": { backgroundImage: `url(${bgImageWebp})` },
                "html.avif &": { backgroundImage: `url(${bgImageAvif})` },
                backgroundSize: "contain",
                backgroundPosition: "50% 0",
              },
            }}
          />
          <Page>
            <Y2cNavbar />
            <Box sx={{ flex: "1 0 auto", position: "relative" }}>
              <AutoAsk />
              <Router />
            </Box>
            <Footer />
          </Page>
        </AuthProvider>
      </trpcContext.TRPCProvider>
    </QueryClientProvider>
  </AppWrapper>
);

const Y2cNavbar = () => {
  const { t } = useTranslation("Header");
  const { isFedAdmin } = useAuth();
  const teamSearchProps = useTeamSearch();

  return (
    <Navbar renderHeader={() => <Header />}>
      <AccountButton>
        {({ user, onClose }) => (
          <Box>
            <MenuItem
              onClick={onClose}
              component={Link}
              to={generatePath("myPage", {
                memberId: user.profile.sub,
              })}
            >
              {t("myPage")}
            </MenuItem>
            {isFedAdmin && (
              <TeamSearchMenuItem onClick={onClose} {...teamSearchProps} />
            )}
            <Divider />
          </Box>
        )}
      </AccountButton>
      <HeaderMenu />
      {isFedAdmin && <TeamSearch {...teamSearchProps} />}
    </Navbar>
  );
};
